// Contrail V3.9.0 — understand a location typed or pasted by a person: decimal degrees, degrees-minutes(-seconds),
// N/S/E/W letters, or a Google Maps link. Used by the map's "Find a location" tool and /api/resolve-link.
// Returns null when nothing sensible is found. Short links (maps.app.goo.gl, goo.gl/maps) need the server to follow them.

export type Found = { lat: number; lng: number; source: "decimal" | "dms" | "google" };

const ok = (lat: number, lng: number) => Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);
const round = (n: number) => Math.round(n * 1e7) / 1e7;

export const isShortMapsLink = (s: string) => /^(https?:\/\/)?(maps\.app\.goo\.gl|goo\.gl\/maps|g\.co\/kgs)\//i.test(s.trim());
export const isGoogleMapsLink = (s: string) => /^(https?:\/\/)?((www\.|maps\.)?google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(s.trim());

/** Coordinates inside a Google Maps URL. The pin (!3d…!4d…, q=, query=, ll=, /place/lat,lng) wins over the view centre (@lat,lng). */
export function fromGoogleUrl(raw: string): Found | null {
  let s = raw.trim();
  try { s = decodeURIComponent(s); } catch { /* keep as is */ }
  const num = "(-?\\d{1,3}(?:\\.\\d+)?)";
  const tries: RegExp[] = [
    new RegExp(`!3d${num}!4d${num}`),
    new RegExp(`[?&](?:q|query|destination|daddr|ll|center|sll)=(?:loc:)?\\s*${num}\\s*,\\s*${num}`),
    new RegExp(`/place/\\s*${num}\\s*,\\s*${num}`),
    new RegExp(`/search/\\s*${num}\\s*,\\s*${num}`),
    new RegExp(`@${num},${num}`),
  ];
  // directions: the destination is the last "lat,lng" stop before the view centre
  if (/\/dir\//.test(s)) {
    const path = s.split(/\/@|\?|\/data=/)[0];
    const stops = [...path.matchAll(new RegExp(`/\\s*${num}\\s*,\\s*${num}`, "g"))];
    const last = stops[stops.length - 1];
    if (last && ok(+last[1], +last[2])) return { lat: round(+last[1]), lng: round(+last[2]), source: "google" };
  }
  for (const re of tries) {
    const m = s.match(re);
    if (m && ok(+m[1], +m[2])) return { lat: round(+m[1]), lng: round(+m[2]), source: "google" };
  }
  return null;
}

/** One coordinate written as degrees [minutes [seconds]] with an optional hemisphere letter. */
function part(txt: string): { v: number; hemi: string | null } | null {
  const t = txt.trim().toUpperCase().replace(/[º˚]/g, "°").replace(/[′’']/g, "'").replace(/[″”"]|''/g, '"');
  const hemi = (t.match(/[NSEW]/) ?? [null])[0];
  const nums = t.replace(/[NSEW]/g, " ").match(/-?\d+(?:[.,]\d+)?/g)?.map((x) => Number(x.replace(",", ".")));
  if (!nums?.length || nums.length > 3) return null;
  const [d, m = 0, sec = 0] = nums;
  if (m < 0 || m >= 60 || sec < 0 || sec >= 60) return null;
  if (nums.length > 1 && !Number.isInteger(d)) return null;
  let v = Math.abs(d) + m / 60 + sec / 3600;
  if (d < 0 || (d === 0 && t.trim().startsWith("-")) || hemi === "S" || hemi === "W") v = -v;
  return { v, hemi };
}

/** Split "lat, lng" in its many forms into two halves. */
function halves(s: string): [string, string] | null {
  const t = s.trim();
  // hemisphere letters mark the split: "17°45'S 177°26'E", "S17.75 E177.44"
  const lettered = t.match(/^(.*?[NS])\s*[,;/]?\s*(.*?[EW]\s*)$/i) ?? t.match(/^([NS].*?)\s*[,;/]?\s*([EW].*)$/i);
  if (lettered && lettered[1].trim() && lettered[2].trim()) return [lettered[1], lettered[2]];
  if (/[,;]/.test(t)) {
    // "-17.7553, 177.4431" — but a decimal comma ("-17,7553 177,4431") has no space after it
    const bySemi = t.split(/\s*;\s*/);
    if (bySemi.length === 2) return [bySemi[0], bySemi[1]];
    const byComma = t.split(/\s*,\s+|\s*,(?=\s*-?\d+(?:\.\d+)?\s*°?\s*$)/);
    if (byComma.length === 2) return [byComma[0], byComma[1]];
    const plain = t.split(",");
    if (plain.length === 2) return [plain[0], plain[1]];
  }
  const nums = t.split(/\s+/);
  if (nums.length === 2) return [nums[0], nums[1]];
  // "17 45 19 S 177 26 35 E" without letters splits in the middle
  if (nums.length === 4 || nums.length === 6) return [nums.slice(0, nums.length / 2).join(" "), nums.slice(nums.length / 2).join(" ")];
  return null;
}

export function parseCoords(input: string): Found | null {
  const s = input.trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s) || isGoogleMapsLink(s)) return fromGoogleUrl(s);
  const h = halves(s);
  if (!h) return null;
  const a = part(h[0]), b = part(h[1]);
  if (!a || !b) return null;
  let lat = a.v, lng = b.v;
  if ((a.hemi === "E" || a.hemi === "W") || (b.hemi === "N" || b.hemi === "S")) [lat, lng] = [lng, lat];      // written lng first with letters
  else if (!a.hemi && !b.hemi && Math.abs(lat) > 90 && Math.abs(lng) <= 90) [lat, lng] = [lng, lat];           // "177.44, -17.75"
  if (!ok(lat, lng)) return null;
  const dmsLike = /[°'"]/.test(s) || (s.match(/\d+(?:[.,]\d+)?/g)?.length ?? 0) > 2;
  return { lat: round(lat), lng: round(lng), source: dmsLike ? "dms" : "decimal" };
}
